// ============================================================================
// /onboarding — post-signup intent picker.
//
// Replaces the previous auto-redirect into the Wayfaring Inn (Threshold). New
// signups were churning fast because they came expecting a chat partner and
// got dropped into a literary narrative. This page asks first, routes second.
//
// Two paths:
//   - Roleplay  → opens CreateWorldWizard locked to companion mode.
//   - Story     → opens CreateWorldWizard at the existing prompt/guided/
//                 templates picker (companion option suppressed since the
//                 user already chose Story over Roleplay).
//
// Each card carries 2-3 lines of guided notes. The wizards themselves
// carry deeper coaching once you're inside.
// ============================================================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/login', { replace: true });
  }, [authLoading, isAuthenticated, navigate]);

  const choose = (intent: 'companion' | 'narrative') => {
    navigate('/', { replace: true, state: { openWizard: intent } });
  };

  return (
    <div className="min-h-screen bg-dark-900 px-4 py-12 flex items-start justify-center">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-dark-50 mb-3">
            What brings you here?
          </h1>
          <p className="text-dark-300 max-w-2xl mx-auto">
            Pick the experience you want. You can always make more later — this just
            decides where we drop you first.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <button
            onClick={() => choose('companion')}
            className="group text-left p-6 rounded-lg bg-dark-700 hover:bg-dark-600 border border-dark-500 hover:border-primary-500/60 transition-all"
          >
            <div className="text-4xl mb-3">🤝</div>
            <h2 className="text-xl font-semibold text-dark-50 mb-2">
              Roleplay with an AI character
            </h2>
            <p className="text-sm text-dark-200 mb-3 leading-relaxed">
              Build a single AI character to talk with — friend, partner, mentor, whatever
              fits. They remember your conversations, develop their own voice, and respond
              like a person, not a story.
            </p>
            <p className="text-xs text-dark-400 leading-relaxed">
              Pick this if you want 1:1 conversation. Think texting a character, not playing
              a game.
            </p>
            <div className="mt-4 text-primary-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              Build a companion →
            </div>
          </button>

          <button
            onClick={() => choose('narrative')}
            className="group text-left p-6 rounded-lg bg-dark-700 hover:bg-dark-600 border border-dark-500 hover:border-primary-500/60 transition-all"
          >
            <div className="text-4xl mb-3">📖</div>
            <h2 className="text-xl font-semibold text-dark-50 mb-2">
              Play an interactive story
            </h2>
            <p className="text-sm text-dark-200 mb-3 leading-relaxed">
              Build or pick a world and play through it as a character. The AI runs the
              setting — NPCs, scenes, consequences — and you make choices in prose. Pick a
              hand-crafted premise, write your own concept, or build it step-by-step.
            </p>
            <p className="text-xs text-dark-400 leading-relaxed">
              Pick this if you want a world to inhabit. Think collaborative novel, not chat.
            </p>
            <div className="mt-4 text-primary-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              Start a story →
            </div>
          </button>
        </div>

        <div className="text-center mt-10">
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-dark-400 hover:text-dark-200 text-sm"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
